import React from "react";

import type { Terms } from "@busbud/busbud-terms";
import type { PublicTerms } from "@busbud/core-types/dist/lib/poll-service/api";
import InfoMD from "@busbud/design-system-components/dist/Icons/Info/MD";
import Text from "@busbud/design-system-components/dist/Text";

import { staticClsx } from "@app/components/tss/tss";
import type { ErrorModalButtonType } from "@app/helpers/api-error";
import { useAppContext } from "@app/helpers/hooks";
import { helpUrl } from "@app/services/url-builders/company-pages-url-builder";
import { useModalActions } from "@app/store/hooks/useModalActions";
import { redirect } from "@app/utils/window";

import type { Terms as NapiTerms } from "app/types/napi-types";

import { Markdown } from "../../text-formatting/markdown";
import { CancelDialog } from "../cancel-trip/cancel-dialog";

interface Props {
  terms: NapiTerms | PublicTerms;
  trip_type: "roundtrip" | "one-way";
}

function isReactNodeArray(node: unknown): node is React.ReactNode[] {
  return React.isValidElement(node) || Array.isArray(node);
}

function renderSection(
  terms: Terms,
  index: number,
  depth = 0
): React.ReactNode {
  const is_subsection = depth > 0;
  const content = renderContent(terms, depth);
  if (!content || (Array.isArray(content) && content.length === 0)) {
    return null;
  }

  const spacingStyle = is_subsection ? "gap-100" : "gap-250";
  return isReactNodeArray(content) ? (
    <div className={staticClsx("flex flex-col", spacingStyle)} key={index}>
      {content}
    </div>
  ) : (
    content
  );
}

function renderContent(terms: Terms, depth: number): React.ReactNode {
  if (terms.subsections) {
    return terms.subsections.map((terms_subsection, index) =>
      renderSection(terms_subsection, index, depth + 1)
    );
  } else if (terms.content) {
    return renderTermsContent(terms.content, terms.level);
  }
}

function renderTermsContent(
  content: string[] | string,
  level?: "info"
): React.ReactNode {
  if (content === "" || level === "info") {
    return null;
  }

  const raw_content = Array.isArray(content) ? content : [content];

  return (
    <div className="flex flex-col gap-100">
      {raw_content
        .filter(item => item !== "")
        .map((item, index) => (
          <Markdown
            key={index}
            content={item}
            link_target_blank
            body_font_size={{ initial: "sm", md: "md" }}
          />
        ))}
    </div>
  );
}

const getMessage = (refund_and_exchange_terms: Terms) => {
  if (!refund_and_exchange_terms || !refund_and_exchange_terms.subsections) {
    return "Error";
  }
  const refund_section = refund_and_exchange_terms.subsections[0];

  // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
  return renderSection(refund_section, 0, 0);
};

export const RefundTerms: React.FC<Props> = ({ terms, trip_type }) => {
  const { closeModal } = useModalActions();
  const { termsBuilder, translator, locale } = useAppContext();
  const refund_and_exchange_terms = termsBuilder.buildRefundAndExchangeTerms({
    terms,
    trip_type
  });

  const onAction = (button_type: ErrorModalButtonType) => {
    if (button_type === "primary") {
      return redirect(helpUrl(locale), true);
    }
    return closeModal();
  };

  return (
    <CancelDialog
      title={translator.t("!about.refunds.title")}
      title_icon={
        <InfoMD width={20} height={20} color="palette.blue.regular.500" />
      }
      message={
        <Text size="lg" color="text.secondary.opaque" align="center">
          {getMessage(refund_and_exchange_terms)}
        </Text>
      }
      primary_button_config={{
        label: translator.t("!purchasepage.purchase.contact-tab-label")
      }}
      secondary_button_config={{
        label: translator.t("!modal.cta.close")
      }}
      actionHandler={onAction}
      onClose={closeModal}
    />
  );
};
